import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useTimezone } from 'context/TimezoneContext';
import { useQueryClient } from '@tanstack/react-query';
import { getLogDetails } from 'api/routes/routesApi';
import { useRouteLogs } from 'api/routes/useRouteLogs';
import { queryKeys } from 'api/queryKeys';
import { parseDuration } from 'utils/parseDuration';
import {
  filterLogDate,
  filterLogStatus,
  filterLogType,
  logGlobalSearch,
  resetLogFilters,
} from 'components/activityLogs/ActivityLogsHelperFunctions';
import type { RouteLogProps } from './RouteDetailsLog';

type FilterTags = {
  name: string;
  id: string;
};
type LogFilterTypes = {
  log_level: FilterTags[];
  log_type: FilterTags[];
  from_date: FilterTags[];
  to_date: FilterTags[];
  search: string;
  ordering: string;
};

// What the page supplies; RouteDetailsLog gets the rest from this hook.
export type RouteLogsTabProps = Omit<
  RouteLogProps,
  'pageLoaded' | 'defaultMinimumWarning'
>;

// The Logs tab's state: its filters, the debounced query they drive and
// every handler RouteDetailsLog calls. The page calls this hook
// (rather than the tab mounting it) so the filters survive switching to
// another tab and back.
export const useRouteLogsTab = ({
  routeId,
  providerId,
  destinationIds,
  active,
  loadedRoute,
}: {
  routeId: string;
  providerId: string | undefined;
  destinationIds: string[];
  // The Logs tab is the open one.
  active: boolean;
  // The Route as the server last returned it (null until it has), so a
  // reload is seen in the same render the data arrives in.
  loadedRoute: { status: string | undefined; loadedAt: number } | null;
}): RouteLogsTabProps => {
  const { t } = useTranslation();
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  const [logsFilter, setLogsFilter] = useState<LogFilterTypes>(() => {
    const threeHoursAgo = new Date();
    threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);

    return {
      log_type: [],
      log_level: [],
      from_date: [
        {
          name: t('Start Date & Time'),
          id: threeHoursAgo.toISOString(),
          attribute_name: 'datetime',
        },
      ],
      to_date: [],
      search: '',
      ordering: '-created_at',
    };
  });

  // Unhealthy Route: whether the Warning-minimum default was already seeded
  // into logsFilter this page visit. A ref, not state: it guards a one-shot
  // and must survive the route reloads that re-run it.
  const warningDefaultSeeded = useRef(false);
  // The Route load the effect below last handled.
  const handledLoadAt = useRef(0);
  // The app-wide timezone the nav sets, so the date filter matches the
  // timestamps in the list.
  const { timezone } = useTimezone();

  // The filter the logs are actually queried with. logsFilter updates as the
  // user types or picks; this copy catches up 300 ms later (or at once for
  // sort, refresh and the unhealthy default), so typing doesn't fire a request
  // per keystroke. While the two differ the list shows its loading state.
  const [logsQueryFilter, setLogsQueryFilter] =
    useState<LogFilterTypes>(logsFilter);
  const commitLogsFilterDebounced = useMemo(
    () => debounce(setLogsQueryFilter, 300),
    [],
  );
  const commitLogsFilterNow = (filter: LogFilterTypes) => {
    commitLogsFilterDebounced.cancel();
    setLogsFilter(filter);
    setLogsQueryFilter(filter);
  };
  // Any other change to logsFilter (search, type, level, date) is queried
  // once it settles. Watching the state, not the handlers, keeps two quick
  // updates in one tick (the date picker sends from and to) from losing one.
  useEffect(() => {
    if (logsFilter !== logsQueryFilter) commitLogsFilterDebounced(logsFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsFilter]);
  useEffect(
    () => () => commitLogsFilterDebounced.cancel(),
    [commitLogsFilterDebounced],
  );

  // Each time the Route (re)loads. Unhealthy Route: seed the Warning-minimum
  // default before the first logs fetch, so a deep link to the Logs tab runs
  // ONE filtered query instead of an unfiltered fetch that the filter panel's
  // own default immediately aborts and redoes. Once per page visit, and never
  // over a level the user already picked; LogsFilter's per-mount default stays
  // the re-apply path after a user clears it and returns to the tab.
  const loadedAt = loadedRoute?.loadedAt ?? 0;
  const needsWarningDefault =
    loadedRoute?.status === 'unhealthy' &&
    !warningDefaultSeeded.current &&
    logsFilter.log_level.length === 0;
  // Logs wait only while this load's seed is still to run: once the effect
  // has handled the load, a level cleared later must not hold them back.
  const waitingForWarningDefault =
    needsWarningDefault && handledLoadAt.current !== loadedAt;
  useEffect(() => {
    if (!loadedAt) return;
    handledLoadAt.current = loadedAt;
    if (needsWarningDefault) {
      warningDefaultSeeded.current = true;
      commitLogsFilterNow(filterLogStatus({ value: '30' }, logsFilter, t));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAt]);

  const logsQuery = useMemo(
    () => ({
      ordering: logsQueryFilter.ordering,
      log_level: logsQueryFilter.log_level.map((a) => a.id).toString(),
      log_type: logsQueryFilter.log_type.map((a) => a.id).toString(),
      from_date:
        logsQueryFilter.from_date.length > 0
          ? logsQueryFilter.from_date[0].id
          : '',
      to_date:
        logsQueryFilter.to_date.length > 0 ? logsQueryFilter.to_date[0].id : '',
      search: logsQueryFilter.search,
    }),
    [logsQueryFilter],
  );
  // integration__in takes INTEGRATION ids. The URL's route_id is a route
  // RECORD id since the decoupling, so the provider's own id has to come
  // from the route — pushing route_id dropped every provider-side log entry
  // (the bulk of a pull Route's logs).
  const destinationIdsKey = destinationIds.join(',');
  const logsIntegrationIds = useMemo(
    () => (providerId ? [...destinationIds, providerId] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [destinationIdsKey, providerId],
  );
  const routeLogs = useRouteLogs({
    routeId,
    integrationIds: logsIntegrationIds,
    query: logsQuery,
    enabled:
      active && logsFilter === logsQueryFilter && !waitingForWarningDefault,
  });

  // Leaving the logs tab drops the pages it scrolled through, so coming back
  // (tab click, back navigation) starts from the newest page again.
  const wasActive = useRef(active);
  useEffect(() => {
    if (wasActive.current && !active && routeId) {
      queryClient.resetQueries({ queryKey: queryKeys.routes.logs(routeId) });
    }
    wasActive.current = active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const handleRefreshLogs = () => {
    console.log('Refreshing logs');

    // If a relative duration is active, recalculate from_date from "now"
    try {
      const raw = sessionStorage.getItem('routeLogsDateFilter');
      if (raw) {
        const saved = JSON.parse(raw);
        const durationInput =
          saved.type === 'relative'
            ? saved.input
            : saved.type === 'preset' && saved.value !== 'custom'
              ? saved.value
              : null;
        if (durationInput) {
          const freshDate = parseDuration(durationInput);
          if (freshDate) {
            // A new from_date is a new query, which starts from the
            // first page on its own.
            const freshFromDate = {
              name: t('Start Date & Time'),
              id: freshDate.toISOString(),
              attribute_name: 'datetime',
            };
            commitLogsFilterNow({ ...logsFilter, from_date: [freshFromDate] });
            return;
          }
        }
      }
    } catch {
      /* ignore parse errors */
    }

    // Same filter: start over from the newest page rather than refetching
    // every page infinite scroll has loaded.
    if (routeId)
      queryClient.resetQueries({ queryKey: queryKeys.routes.logs(routeId) });
  };

  const handleNextLogPageClick = () => {
    if (routeLogs.nextCursor && !routeLogs.loading) routeLogs.fetchNextPage();
  };

  const handleSortLogTimestamp = (direction: boolean) => {
    console.log('Sorting logs by timestamp in direction: ', direction);
    commitLogsFilterNow({
      ...logsFilter,
      ordering: direction ? 'created_at' : '-created_at',
    });
  };

  const handleGlobalLogSearch = (searchSubStr: string) => {
    const updatedFilter = logGlobalSearch(searchSubStr, logsFilter);
    setLogsFilter(updatedFilter);
  };

  const handleFilterLogType = (e: any) => {
    console.log('Filtering logs by type: ', e);
    const updatedFilter = filterLogType(e, logsFilter, t);
    setLogsFilter(updatedFilter);
  };

  const handleFilterLogStatus = (e: any) => {
    console.log('Filtering logs by status: ', e);
    const updatedFilter = filterLogStatus(e, logsFilter, t);
    setLogsFilter(updatedFilter);
  };

  const handleFilterLogsByDate = (e: any, side: string) => {
    console.log('Filtering logs by date: ', e, ' side: ', side);
    setLogsFilter((prevFilter) => filterLogDate(e, side, prevFilter, t));
  };

  function handleResetLogFilters() {
    console.log('Resetting log filters');
    const updatedFilter = resetLogFilters();
    setLogsFilter(updatedFilter);
  }

  async function handleSelectLog(log_id: string) {
    const token = keycloak.token;
    if (
      log_id &&
      token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired()
    ) {
      const temp = await getLogDetails(token, log_id);
      return temp;
    }
  }

  return {
    logs: routeLogs.logs,
    filters: logsFilter.log_level.concat(
      logsFilter.log_type,
      logsFilter.from_date,
      logsFilter.to_date,
    ),
    globalSearchStr: logsFilter.search,
    reloadLogs: logsFilter !== logsQueryFilter || routeLogs.loading,
    isAppendingLogs: routeLogs.appending,
    nextCursor: routeLogs.nextCursor,
    selectedTimezone: timezone,
    onNextPageClicked: handleNextLogPageClick,
    requestSort: handleSortLogTimestamp,
    onChangeLogGlobalSearch: handleGlobalLogSearch,
    onChangeLogStatus: handleFilterLogStatus,
    onChangeLogType: handleFilterLogType,
    onChangeLogDate: handleFilterLogsByDate,
    onResetLogFilters: handleResetLogFilters,
    onSelectLog: handleSelectLog,
    onRefreshLogs: handleRefreshLogs,
    logsError: routeLogs.error,
    hasActiveLogLevel: logsFilter.log_level.length > 0,
    activeLogType: logsFilter.log_type[0]?.id ?? null,
    activeLogLevel: logsFilter.log_level[0] ?? null,
  };
};
